/** Group domain — SPEC §13 pairwise fan-out + epoch sender keys */

import { GROUP_MAX_MEMBERS_V1, type GroupRole } from "@veto/protocol";
import { VetoError } from "./errors";

export interface GroupMember {
  contactId: string;
  identityKeyHex: string;
  hybridPublicKeyHex: string;
  displayName: string;
  role: GroupRole;
}

export interface GroupRecord {
  groupId: string;
  title: string;
  epoch: number;
  members: GroupMember[];
  /** Local copy of current epoch sender key (base64). Never upload. */
  senderKeyB64: string;
  createdAt: number;
  /** Conversation id used in UI list (= groupId). */
  conversationId: string;
}

export function assertGroupSize(members: GroupMember[]): void {
  if (members.length + 1 > GROUP_MAX_MEMBERS_V1) {
    throw new VetoError(
      "GROUP_TOO_LARGE",
      `Group exceeds max ${GROUP_MAX_MEMBERS_V1} members`,
    );
  }
}

export function createGroupRecord(input: {
  title: string;
  creator: GroupMember;
  members: GroupMember[];
  senderKeyB64: string;
}): GroupRecord {
  assertGroupSize(input.members);
  const groupId = randomId();
  const members = [
    { ...input.creator, role: "admin" as const },
    ...input.members.map((m) => ({ ...m, role: m.role || ("member" as const) })),
  ];
  // dedupe by identity
  const seen = new Set<string>();
  const unique = members.filter((m) => {
    if (seen.has(m.identityKeyHex)) return false;
    seen.add(m.identityKeyHex);
    return true;
  });
  return {
    groupId,
    title: input.title.trim() || "Group",
    epoch: 1,
    members: unique,
    senderKeyB64: input.senderKeyB64,
    createdAt: Date.now(),
    conversationId: groupId,
  };
}

/** FR-GRP-002: removal bumps epoch; caller must supply a fresh sender key. */
export function removeMember(
  group: GroupRecord,
  targetIdentityKeyHex: string,
  newSenderKeyB64: string,
): GroupRecord {
  const target = targetIdentityKeyHex.toLowerCase();
  const nextMembers = group.members.filter((m) => m.identityKeyHex.toLowerCase() !== target);
  if (nextMembers.length === group.members.length) return group;
  return {
    ...group,
    epoch: group.epoch + 1,
    members: nextMembers,
    senderKeyB64: newSenderKeyB64,
  };
}

/**
 * Promote/demote admin — bumps epoch + rotates sender key so peers learn the new
 * admin map via signed group-key fanout (SEC-1-6 residual).
 */
export function setMemberRole(
  group: GroupRecord,
  targetIdentityKeyHex: string,
  role: GroupRole,
  newSenderKeyB64: string,
): GroupRecord {
  const members = group.members.map((m) =>
    m.identityKeyHex === targetIdentityKeyHex ? { ...m, role } : m,
  );
  const admins = members.filter((m) => m.role === "admin");
  if (admins.length < 1) {
    throw new VetoError("VALIDATION_FAILED", "Group must retain at least one admin");
  }
  return {
    ...group,
    epoch: group.epoch + 1,
    members,
    senderKeyB64: newSenderKeyB64,
  };
}

/** Rename (admins): a new epoch with a fresh sender key, like any membership change. */
export function renameGroup(group: GroupRecord, title: string, newSenderKeyB64: string): GroupRecord {
  const clean = title.replace(/[\u0000-\u001f\u007f\u202a-\u202e\u2066-\u2069]/g, "").trim().slice(0, 64);
  if (!clean) throw new VetoError("VALIDATION_FAILED", "Give the group a name");
  return { ...group, title: clean, epoch: group.epoch + 1, senderKeyB64: newSenderKeyB64 };
}

export function addMembers(
  group: GroupRecord,
  newMembers: GroupMember[],
  newSenderKeyB64: string,
): GroupRecord {
  const map = new Map(group.members.map((m) => [m.identityKeyHex, m]));
  for (const m of newMembers) map.set(m.identityKeyHex, m);
  const members = [...map.values()];
  assertGroupSize(members.slice(1)); // rough
  if (members.length > GROUP_MAX_MEMBERS_V1) {
    throw new VetoError(
      "GROUP_TOO_LARGE",
      `Group exceeds max ${GROUP_MAX_MEMBERS_V1} members`,
    );
  }
  return {
    ...group,
    epoch: group.epoch + 1,
    members,
    senderKeyB64: newSenderKeyB64,
  };
}

/** FR-GRP-002 helper: can this key material decrypt messages for epoch? */
export function canDecryptGroupEpoch(
  heldEpoch: number,
  heldKeyB64: string | undefined,
  messageEpoch: number,
): boolean {
  if (!heldKeyB64) return false;
  return heldEpoch === messageEpoch;
}

/** Local admin gate for membership mutations (UI + action layer). */
export function assertSelfIsAdmin(group: GroupRecord, selfIdentityKeyHex: string): void {
  const self = group.members.find((m) => m.identityKeyHex === selfIdentityKeyHex);
  if (!self || self.role !== "admin") {
    throw new VetoError("PERMISSION_DENIED", "Only group admins may change membership or roles");
  }
}

export type GroupKeyDecision =
  | { ok: true; mode: "create" | "advance" | "idempotent" }
  | { ok: false; reason: string; keepPrior: true; fork?: boolean };

/**
 * FR-GRP-001/002/005 — decide whether an inbound group-key may replace local state.
 * Never installs epoch rollback or conflicting same-epoch keys.
 * Sender must be admin in the *prior* membership (or listed admin on first create).
 */
export function evaluateInboundGroupKey(opts: {
  prior: GroupRecord | undefined;
  epoch: number;
  /** Wire or local members — only identity + role are consulted. */
  members: { identityKeyHex: string; role: GroupRole }[];
  senderIdentityKeyHex: string;
  senderKeyB64: string;
}): GroupKeyDecision {
  const { prior, epoch, members, senderIdentityKeyHex, senderKeyB64 } = opts;
  if (!senderIdentityKeyHex || epoch < 1) {
    return { ok: false, reason: "invalid-group-key", keepPrior: true };
  }

  if (!prior) {
    // Late join may first see epoch > 1 (FR-GRP-003); still require listed admin.
    const sender = members.find(
      (m) => m.identityKeyHex.toLowerCase() === senderIdentityKeyHex.toLowerCase(),
    );
    if (!sender || sender.role !== "admin" || epoch < 1) {
      return { ok: false, reason: "sender-not-admin-on-create", keepPrior: true };
    }
    return { ok: true, mode: "create" };
  }

  const priorSender = prior.members.find(
    (m) => m.identityKeyHex.toLowerCase() === senderIdentityKeyHex.toLowerCase(),
  );
  if (!priorSender || priorSender.role !== "admin") {
    return { ok: false, reason: "sender-not-admin", keepPrior: true };
  }

  if (epoch < prior.epoch) {
    return { ok: false, reason: "epoch-rollback", keepPrior: true };
  }

  if (epoch === prior.epoch) {
    const a = new Set(prior.members.map((m) => m.identityKeyHex.toLowerCase()));
    const b = new Set(members.map((m) => m.identityKeyHex.toLowerCase()));
    const sameMembers = a.size === b.size && [...a].every((k) => b.has(k));
    if (!sameMembers) {
      return { ok: false, reason: "membership-fork", keepPrior: true, fork: true };
    }
    if (senderKeyB64 !== prior.senderKeyB64) {
      return { ok: false, reason: "key-conflict-same-epoch", keepPrior: true, fork: true };
    }
    return { ok: true, mode: "idempotent" };
  }

  // Sequential +1 or catch-up jump after missed removal epochs (re-add / FR-GRP-003).
  if (epoch > prior.epoch) {
    return { ok: true, mode: "advance" };
  }

  return { ok: false, reason: "invalid-epoch", keepPrior: true };
}

function randomId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}
